import * as React from 'react';
import { grey, red, blue} from '@mui/material/colors';
import { 
  Modal, Typography, Box, Backdrop, Fade, 
  ListItemText, Button, FormControl, TextField, MenuItem, 
  Select, InputAdornment, CircularProgress, IconButton, Snackbar, Alert 
} from "@mui/material";
import { useState, useEffect } from "react";
import EditIcon from '@mui/icons-material/EditOutlined';
import ErrorIcon from '@mui/icons-material/Error';
import BASE_URL from '../../config'

const style = {
  position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: '500px' },  height: 'auto', bgcolor: 'background.paper',
  boxShadow: 24, borderRadius: 4, p: 4, overflowY: 'auto', outline: 'none',
  display: 'flex', flexDirection: 'column'
};

const Inputstyle = {
  '& .MuiOutlinedInput-root': {
    bgcolor: '#f5f5f5', 
    borderRadius: '8px',
    // Borde normal
    '& fieldset': { borderColor: 'transparent' },
    '&:hover fieldset': { borderColor: grey[400] },
    '&.Mui-focused fieldset': { borderColor: grey[600] },
    
    // --- AÑADE ESTO PARA EL ERROR ---
    '&.Mui-error fieldset': { 
      borderColor: red[700], // O el color rojo que prefieras
      borderWidth: '1px' 
    },
  },
};


export default function Popedituser({ user: userData, refresh }) {
  const [open, setOpen] = useState(false);
  const [rolesDB, setRolesDB] = useState([]);
  const [error, setError] = useState('');
  const [showErrors, setShowErrors] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirmType, setConfirmType] = useState(null); // 'save' o 'cancel'
  
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [user, setUser] = useState({
    nombre_completo: '', email: '', phone: '', role: '', estado: ''
  });

  const isUsenameValid = user.nombre_completo.trim().length > 6;
  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(user.email);
  const isPhoneValid = user.phone.length === 10;
  const isFormComplete = isUsenameValid && isEmailValid && isPhoneValid && user.role !== '' && user.estado !== '';

  const handleClose = () => {
    setConfirmType(null);
    setOpen(false);
    setLoading(false);
    setShowErrors(false);
    setError('');
  };

  useEffect(() => {
    if (userData && open) {
      setUser({
        nombre_completo: userData.nombre_completo || '',
        email: userData.email || '',
        phone: userData.phone || '',
        role: userData.nombre_rol || '',
        estado: userData.estado ?? ''
      });
      setShowErrors(false);
      setConfirmType(null);
      setError('');
    }
  }, [userData, open]);

  useEffect(() => {
    if (open) {
      fetch(`${BASE_URL}/roles`)
        .then(res => res.json())
        .then(data => setRolesDB(data))
        .catch(() => setSnackbar({ open: true, message: 'No se pudieron cargar los roles', severity: 'error' }));
    }
  }, [open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const limits = {
    nombre_completo: 35,
    email: 30,
  };
  
  if (limits[name] && value.length > limits[name]) {
    return;
  }

    if (name === 'phone') {
      const onlyNums = value.replace(/[^0-9]/g, '');
      if (onlyNums.length <= 10) setUser({ ...user, [name]: onlyNums });
      return;
    }
    setUser({ ...user, [name]: value });
  };

  const validateAndOpenSave = async() => {
    setShowErrors(true);
    setError('');

    if (!isFormComplete) return; 

    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/users/checkmail`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
                email: user.email, 
                userId: userData.id 
            })
        });
      
      const data = await res.json();
      
      if (res.ok) {
        setConfirmType('save');
      } else {
        setError(data.message || 'El correo ya está registrado.');
      }
    } catch (e) {
      setError('Error de conexión con el servidor.');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${BASE_URL}/users/${userData.id}`, {
        method: 'PUT',
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(user),
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setSnackbar({ open: true, message: 'Usuario actualizado con éxito', severity: 'success' });
        setTimeout(() => {
          if(refresh) refresh();
          handleClose();
        }, 1000); 
      } else {
        setError(data.message || 'Error al actualizar el usuario.');
        setConfirmType(null);
      }
    } catch (e) {
      setError('Error al conectar con el servidor.');
      setConfirmType(null);
    } finally {
      setLoading(false);
    }
  };

  const ConfirmView = () => (
    <Box sx={style}>
      <Typography variant="h6">
        {confirmType === 'cancel' ? '¿Cancelar edición?' : '¿Guardar cambios?'}
      </Typography>
      <Typography variant="body1" color='textSecondary' sx={{ mt: 1, mb: 3 }}>
        {confirmType === 'cancel' 
          ? '¿Estás seguro que deseas cancelar? Los cambios realizados se perderán.' 
          : '¿Estás seguro que deseas aplicar los cambios a este usuario?'}
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'end' }}>
        <Button 
          variant="contained" 
          sx={{ bgcolor: grey[200], color: 'black', textTransform: 'none', px: 3, borderRadius: '8px'}} 
          onClick={() => setConfirmType(null)} // "No, revisar" -> vuelve al formulario
          disabled={loading}
        >
          {confirmType === 'cancel' ? 'No, continuar' : 'No, revisar'}
        </Button>
        <Button 
          variant="contained" 
          sx={{ bgcolor: 'black', color: 'white', textTransform: 'none', px: 3, borderRadius: '8px'}}
          onClick={confirmType === 'cancel' ? handleClose : handleUpdate}
          disabled={loading}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : (confirmType === 'cancel' ? 'Sí, cancelar' : 'Sí, guardar')}
        </Button>
      </Box>
    </Box>
  );

  return (
    <>
      <IconButton onClick={() => setOpen(true)} 
      sx={{'&:hover': {color:grey[100], bgcolor: blue[400] } }}>
        <EditIcon fontSize="small" />
      </IconButton>

      <Modal open={open} onClose={() => !loading && setConfirmType('cancel')} closeAfterTransition slots={{ backdrop: Backdrop }}>
        <Fade in={open}>
          <Box>
            {confirmType ? <ConfirmView /> : (
              <Box sx={style}>
                <Box sx={{ mb: 2 }}>
                  {error && (
                    <Box sx={{ bgcolor: red[50], p: 1.5, borderRadius: '8px', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ErrorIcon sx={{ color: red[700], fontSize: 20 }} />
                      <Typography sx={{ color: red[700], fontSize: '0.875rem', fontWeight: 500 }}>{error}</Typography>
                    </Box>
                  )}
                  <Typography variant="h6">Editar Usuario</Typography>
                  <Typography variant="body2" color='textSecondary'>Actualiza la información del usuario</Typography>
                </Box>
                
                {/* NOMBRE COMPLETO */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <FormControl error={showErrors && user.nombre_completo === ''}>
                    <Typography variant="body2" fontWeight="500" sx={{ mb: 0.5 }}>Nombre Completo</Typography>
                    <TextField size="small" sx={Inputstyle} fullWidth name='nombre_completo' onChange={handleChange} value={user.nombre_completo}
                      error={(user.nombre_completo.length > 0 && !isUsenameValid) || (showErrors && user.nombre_completo === '')}
                      helperText={user.nombre_completo.length > 0 && !isUsenameValid ? "Ingresa tu nombre completo" : (showErrors && user.nombre_completo === '' ? "Campo obligatorio" : "")}

                      />
                  </FormControl>

                  {/* EMAIL */}
                  <FormControl error={showErrors && !isEmailValid}>
                    <Typography variant="body2" fontWeight="500" sx={{ mb: 0.5 }}>Correo Electrónico</Typography>
                    <TextField 
                      size="small" sx={Inputstyle} fullWidth name='email' onChange={handleChange} value={user.email}
                      error={(user.email.length > 0 && !isEmailValid) || (showErrors && user.email === '')}
                      helperText={user.email.length > 0 && !isEmailValid ? "Ingresa un correo válido" : (showErrors && user.user_pass === '' ? "Campo obligatorio" : "")}

                    />
                  </FormControl>

                  {/* NUMERO TELEFONICO */}
                  <FormControl error={showErrors && !isPhoneValid}>
                    <Typography variant="body2" fontWeight="500" sx={{ mb: 0.5 }}>Teléfono</Typography>
                    <TextField 
                      size="small" sx={Inputstyle} fullWidth name='phone' onChange={handleChange} value={user.phone}
                      error={showErrors && !isPhoneValid}
                      helperText={showErrors && !isPhoneValid ? "Se requiere un número telefónico 10 dígitos" : ""}
                      InputProps={{ startAdornment: <InputAdornment position="start">+52</InputAdornment> }}
                    />
                  </FormControl>

                  {/* ROLES */}
                  <FormControl error={showErrors && user.role === ''}>
                    <Typography variant="body2" fontWeight="500" sx={{ mb: 0.5 }}>Rol</Typography>
                    <Select 
                      name='role' 
                      size='small' 
                      value={rolesDB.some(r => r.nombre === user.role) ? user.role : ""} 
                      displayEmpty 
                      onChange={handleChange} 
                      sx={Inputstyle}>
                      <MenuItem disabled value="">Seleccionar una opción</MenuItem>
                      {rolesDB.map((rol) => (<MenuItem key={rol.id} value={rol.nombre}><ListItemText primary={rol.nombre} /></MenuItem>))}
                    </Select>
                  </FormControl>

                  {/* ESTADO */}
                  <FormControl error={showErrors && user.estado === ''}>
                    <Typography variant="body2" fontWeight="500" sx={{ mb: 0.5 }}>Estado</Typography>
                    <Select name='estado' size='small' value={user.estado} displayEmpty onChange={handleChange} sx={Inputstyle}>
                      <MenuItem disabled value="">Seleccionar una opción</MenuItem>
                      <MenuItem value={true}><ListItemText primary="Activo" /></MenuItem>
                      <MenuItem value={false}><ListItemText primary="Inactivo" /></MenuItem>
                    </Select>
                  </FormControl>

                  {/* BOTONES */}
                  <Box sx={{ display: 'flex', mt: 1, justifyContent: 'end', gap: 2 }}>
                    <Button 
                      onClick={() => setConfirmType('cancel')} 
                      sx={{ bgcolor: grey[100], color: 'black', textTransform: 'none', px: 3, borderRadius: '8px', '&:hover': { bgcolor: grey[200] } }}
                    >
                      Cancelar
                    </Button>
                    <Button 
                      variant='contained' 
                      onClick={validateAndOpenSave}
                      disabled={loading}
                      sx={{ bgcolor: 'black', color: 'white', textTransform: 'none', px: 3, borderRadius: '8px', '&:hover': { bgcolor: '#333' } }}
                    >
                      {loading ? <CircularProgress size={20} color="inherit" /> : 'Guardar cambios'}
                    </Button>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </Fade>
      </Modal>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({...snackbar, open: false})} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} variant="filled" sx={{ width: '100%', borderRadius: '10px' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}